import type { CROP_YEAR_FIELD_LIMITS, HelpFieldKey } from "consts";

import type { SoilSightCoverCropDefaults } from "models";

export type CropDetailsNumberFieldKey = keyof typeof CROP_YEAR_FIELD_LIMITS;

export type CropDetailsFieldRule = "positiveRequired" | "percentRange" | "nonNegative";

export interface CropDetailsNumberFieldConfig<Name extends CropDetailsNumberFieldKey = CropDetailsNumberFieldKey> {
  name: Name;
  placeholder: string;
  rule: CropDetailsFieldRule;
  ruleMessageKey: string;
}

/** Economics inputs of the crop details drawer, in display order. */
export const CROP_DETAILS_ECONOMICS_FIELDS: CropDetailsNumberFieldConfig[] = [
  { name: "yield", placeholder: "0.00", rule: "positiveRequired", ruleMessageKey: "cropDetailsDrawer.validation.yieldPositive" },
  { name: "sellingPrice", placeholder: "0.00", rule: "positiveRequired", ruleMessageKey: "cropDetailsDrawer.validation.pricePositive" },
  { name: "fodderLosses", placeholder: "0.0", rule: "percentRange", ruleMessageKey: "cropDetailsDrawer.validation.percentRange" },
  { name: "mouldLosses", placeholder: "0.0", rule: "percentRange", ruleMessageKey: "cropDetailsDrawer.validation.percentRange" },
  { name: "minFertiliser", placeholder: "0.00", rule: "nonNegative", ruleMessageKey: "cropDetailsDrawer.validation.nonNegative" },
  { name: "plantProtection", placeholder: "0.00", rule: "nonNegative", ruleMessageKey: "cropDetailsDrawer.validation.nonNegative" },
  { name: "seedCosts", placeholder: "0.00", rule: "nonNegative", ruleMessageKey: "cropDetailsDrawer.validation.nonNegative" },
  { name: "seedAmount", placeholder: "0.00", rule: "nonNegative", ruleMessageKey: "cropDetailsDrawer.validation.nonNegative" },
];

/** Cover crop inputs shown once a cover crop type is chosen. */
export const CROP_DETAILS_COVER_CROP_FIELDS: CropDetailsNumberFieldConfig<keyof SoilSightCoverCropDefaults>[] = [
  { name: "legumeShare", placeholder: "0.0", rule: "percentRange", ruleMessageKey: "cropDetailsDrawer.validation.percentRange" },
  { name: "ccCost", placeholder: "0.00", rule: "nonNegative", ruleMessageKey: "cropDetailsDrawer.validation.nonNegative" },
];

export const CROP_DETAILS_FIELD_LABEL_KEYS: Record<HelpFieldKey, string> = {
  crop: "cropDetailsDrawer.field.crop",
  yield: "cropDetailsDrawer.field.yield",
  sellingPrice: "cropDetailsDrawer.field.sellingPrice",
  minFertiliser: "cropDetailsDrawer.field.minFertiliser",
  plantProtection: "cropDetailsDrawer.field.plantProtection",
  seedCosts: "cropDetailsDrawer.field.seedCosts",
  seedAmount: "cropDetailsDrawer.field.seedAmount",
  fodderLosses: "cropDetailsDrawer.field.fodderLosses",
  mouldLosses: "cropDetailsDrawer.field.mouldLosses",
  tillageId: "cropDetailsDrawer.field.tillage",
  residue: "cropDetailsDrawer.field.residue",
  irrigation: "cropDetailsDrawer.field.irrigation",
  coverCropId: "cropDetailsDrawer.field.coverCropType",
  legumeShare: "cropDetailsDrawer.field.legumeShare",
  ccCost: "cropDetailsDrawer.field.ccCost",
};

/** Replaced with the user's currency symbol when a unit is shown. */
export const CROP_DETAILS_CURRENCY_TOKEN = "{currency}";

export const CROP_DETAILS_FIELD_UNITS: Partial<Record<HelpFieldKey, string>> = {
  yield: "t/ha",
  sellingPrice: "{currency}/t",
  fodderLosses: "%",
  mouldLosses: "%",
  minFertiliser: "{currency}/kg N",
  plantProtection: "{currency}/ha",
  seedCosts: "{currency}/kg",
  seedAmount: "kg/ha",
  legumeShare: "%",
  ccCost: "{currency}/ha",
};

/** Fodder and mould losses together can't exceed the whole harvest. */
export const CROP_DETAILS_MAX_TOTAL_LOSSES_PCT = 100;
